/**
 * Brazilian CEP (postal code) — 8 digits, no check digit.
 *
 * Display format: XXXXX-XXX (eg. 01310-100)
 */

/**
 * Strip every non-digit. Result is 0–8 chars.
 */
export function cleanCep(value: string): string {
  return value.replace(/\D/g, '');
}

/**
 * Validates the 8-digit length. There is no check digit; further validation
 * (e.g. range lookup) must consult an external service like ViaCEP.
 *
 * isValidCep("01310-100") → true
 * isValidCep("01310100")  → true
 * isValidCep("01310")     → false
 * isValidCep("")          → false
 */
export function isValidCep(value: string): boolean {
  return cleanCep(value).length === 8;
}

/**
 * Display mask: XXXXX-XXX. Inserts the hyphen after the 5th digit.
 *
 * maskCep("01310100") → "01310-100"
 * maskCep("0131")     → "0131"
 */
export function maskCep(value: string): string {
  const digits = cleanCep(value).slice(0, 8);
  if (digits.length <= 5) return digits;
  return `${digits.slice(0, 5)}-${digits.slice(5)}`;
}

/**
 * Endereço devolvido pela consulta de CEP. Campos podem vir vazios — a
 * ViaCEP não preenche `logradouro`/`bairro` para CEPs de cidade inteira
 * (os terminados em -000), e nesses casos o usuário completa na mão.
 */
export interface CepLookupResult {
  street: string;
  neighborhood: string;
  city: string;
  /** UF em duas letras maiúsculas (ex.: "SP"). */
  state: string;
}

/**
 * Consulta um CEP na ViaCEP.
 *
 * Falha graciosamente — **nunca lança**, no molde de `lib/geocode.ts` na API:
 * CEP inexistente, rede fora ou timeout retornam `null`, e a tela apenas
 * deixa o usuário preencher o endereço na mão. Preencher endereço nunca pode
 * travar por causa de um serviço externo de conveniência.
 *
 * Fonte única para web-admin (perfil, onboarding, prestadores), web-partner e
 * o app do gestor — antes eram cinco `fetch` inline com tratamentos sutilmente
 * diferentes do campo `erro`.
 *
 * @param value CEP com ou sem máscara. Não-8-dígitos retorna `null` sem rede.
 * @param timeoutMs Teto da requisição (default 8s).
 */
export async function lookupCep(
  value: string,
  timeoutMs = 8000,
): Promise<CepLookupResult | null> {
  const digits = cleanCep(value);
  if (digits.length !== 8) return null;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(`https://viacep.com.br/ws/${digits}/json/`, {
      signal: controller.signal,
    });
    if (!res.ok) return null;
    const data = (await res.json()) as {
      // A ViaCEP devolve `erro` ora como boolean, ora como a string "true"
      // — daí a normalização abaixo em vez de um `if (data.erro)` direto.
      erro?: boolean | string;
      logradouro?: string;
      bairro?: string;
      localidade?: string;
      uf?: string;
    };
    if (data.erro === true || data.erro === 'true') return null;

    return {
      street: data.logradouro ?? '',
      neighborhood: data.bairro ?? '',
      city: data.localidade ?? '',
      state: (data.uf ?? '').toUpperCase(),
    };
  } catch {
    // Rede fora, timeout (abort) ou JSON inválido — o usuário digita na mão.
    return null;
  } finally {
    clearTimeout(timer);
  }
}
